import { faExclamationCircle as faRegExclamationCircle } from "@fullscript/pro-regular-svg-icons";
import { faCheckCircle, faExclamationCircle } from "@fullscript/pro-solid-svg-icons";
import type { ReactNode } from "react";

import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import { Link } from "@shared/react-router-dom/react-router-dom";

import { Tooltip } from "../Tooltip";
import { Typography } from "../Typography";

import * as styles from "./Stepper.styles";

interface Props {
  /**
   * Specifies the current step you are on
   *
   * @required
   */
  currentStep: number;
  /**
   * An array of objects containing `label` (required) and `tooltipContent` (optional) keys.
   * When the entire array is not provided, you must provide a number of steps instead.
   * Note: the default tooltip content for each step is the `label` - `tooltipContent` will only
   * override this when the current step is active.
   */
  steps: { label: string; tooltipContent?: string; href?: string; to?: string }[] | number;
  /**
   * Specifies whether or not only the current step (status) label is displayed beneath the stepper,
   * or if every step has a label beneath it. If set to true, the past and future steps are displayed as tooltips.
   *
   * @default false
   */
  showStatusLabel?: boolean;
  /**
   * Specifies which direction the status label should be placed
   *
   * @default null
   */
  statusLabelPosition?: "left" | "center";
  /**
   * Specify if there is an error in the progress of the stepper
   *
   * @default false
   */
  isError?: boolean;
  /**
   * Specify if the connector between the steps is a dashed line instead of solid
   *
   * @default false
   */
  isDashed?: boolean;
  /**
   * Specify the size of the atoms and connectors
   *
   * @default "large"
   */
  size?: "small" | "large";
  /**
   * Specifies that the final step is incomplete and should not have a filled step atom
   *
   * @default false
   */
  isIncompleteFinalStep?: boolean;
  /**
   * Hides steps atoms
   *
   * @default false
   */
  hideAtoms?: boolean;
  /**
   * Makes ends of stepper lines rounded
   *
   * @default false
   */
  isRounded?: boolean;
  /**
   * Changes color of completed indicators to dark gray
   *
   * @default false
   */
  isGray?: boolean;
}

/**
 * @deprecated DS4 component — a DS5 `Stepper` replacement is coming soon. Do not add new usages.
 */
const Stepper = ({
  currentStep,
  steps,
  showStatusLabel,
  statusLabelPosition,
  isError,
  isDashed,
  size = "large",
  isIncompleteFinalStep,
  hideAtoms,
  isRounded,
  isGray,
  ...rest
}: Props) => {
  const isCurrent = (step: number) => {
    return step === currentStep;
  };

  const stepsHaveLabels = Array.isArray(steps);
  const stepCount = stepsHaveLabels ? steps.length : steps;

  const isFirstStep = (step: number) => {
    return step === 1;
  };

  const isPreFinalStep = (step: number) => {
    return step === stepCount - 1;
  };

  const isFinalStep = (step: number) => {
    return isCurrent(step) && step === stepCount;
  };

  const isPreviousStep = (step: number) => {
    return step < currentStep;
  };

  const isDashedAndPreviousStep = (step: number) => isDashed && isPreviousStep(step);
  const isCurrentErrorStep = (step: number) => isError && isCurrent(step);
  const isSuccessfulFinalStep = (step: number) =>
    !isError && !isIncompleteFinalStep && isFinalStep(step);

  const prepareCompletedConnectorStyle = () =>
    isGray ? styles.connectors.completedGray : styles.connectors.completed;

  const stepStyle = (step: number) => {
    const dashedStyles = [
      styles.steps.isDashedBase[size],
      (isSuccessfulFinalStep(step) || isPreviousStep(step) || isCurrentErrorStep(step)) &&
        styles.steps.completed,
      isCurrentErrorStep(step) && styles.steps.dashedError,
    ];

    const baseStyles = [
      styles.steps.base,
      styles.steps.size[size],
      isCurrent(step) && styles.steps.current,
      isCurrent(step) && styles.steps.currentBorder.successful[size],
      isPreviousStep(step) && styles.steps.previous,
      isError && isPreviousStep(step) && styles.steps.error,
      isCurrentErrorStep(step) && styles.steps.currentBorder.error[size],
      isSuccessfulFinalStep(step) && styles.steps.completed,
    ];

    return baseStyles.concat(isDashed ? dashedStyles : []);
  };

  const linkStepStyles = [isDashed ? styles.steps.dashedLink : styles.steps.link];

  const connectorStyle = (step: number) => {
    return [
      styles.connectors.base,
      styles.connectors.size[size],
      showStatusLabel ? styles.connectors.labels.status : styles.connectors.labels.all,
      isCurrent(step) && styles.connectors.current,
      isPreviousStep(step) && prepareCompletedConnectorStyle(),
      isError && isPreviousStep(step) && styles.connectors.error,
      isDashed && styles.connectors.dashed.base,
      isDashed && styles.connectors.dashed[size],
      isRounded && isFirstStep(step) && styles.connectors.firstStep,
      isRounded && isPreFinalStep(step) && styles.connectors.finalStep,
    ];
  };

  const stepWrapperStyle = (stepIndex: number) => {
    return [
      styles.stepWrapper.base,
      showStatusLabel ? styles.stepWrapper.labels.status : styles.stepWrapper.labels.all,
      ...connectorStyle(stepIndex),
    ];
  };

  const statusLabelStyle = (step: number) => {
    return [
      statusLabelPosition === "center" && styles.labels.status.centered,
      styles.labels.status.size[size],
      isCurrentErrorStep(step) && styles.labels.error,
    ];
  };

  const renderStepIcon = (step: number) => {
    if (isDashedAndPreviousStep(step) || isSuccessfulFinalStep(step)) {
      return <FontAwesomeIcon icon={faCheckCircle} />;
    }

    if (isDashed && isCurrentErrorStep(step)) {
      return <FontAwesomeIcon icon={faExclamationCircle} />;
    }

    return null;
  };

  const renderErrorLabelIcon = (i: number) => {
    if (isCurrentErrorStep(i + 1)) return <FontAwesomeIcon icon={faRegExclamationCircle} />;

    return null;
  };

  const stepLabels = stepsHaveLabels ? steps.map(step => step.label) : [];

  const renderStep = (i: number) => {
    const stepIndex = i + 1;
    const stepAtIndex = stepsHaveLabels ? steps[i] : undefined;

    const renderTooltip = (child: ReactNode) => {
      if (!stepsHaveLabels) return child;

      const renderTooltipContent = () => {
        if (isCurrent(stepIndex) && stepAtIndex?.tooltipContent) return stepAtIndex?.tooltipContent;

        return stepLabels[i];
      };

      const isTooltipHidden =
        (!showStatusLabel && !isCurrent(stepIndex)) ||
        (!showStatusLabel && isCurrent(stepIndex) && !stepAtIndex?.tooltipContent);

      return (
        <Tooltip
          tooltipContent={renderTooltipContent()}
          placement="top"
          isTooltipHidden={isTooltipHidden}
          data-testid={`tooltip-step-${stepIndex}`}
        >
          {child}
        </Tooltip>
      );
    };

    const shouldRenderHrefLinkAtom = isPreviousStep(stepIndex) && stepAtIndex?.href && !isError;
    const shouldRenderToLinkAtom = isPreviousStep(stepIndex) && stepAtIndex?.to && !isError;
    const renderAtomType = () => {
      if (hideAtoms) return null;

      if (shouldRenderHrefLinkAtom && stepAtIndex) {
        return (
          <a
            href={stepAtIndex.href}
            css={[stepStyle(stepIndex), linkStepStyles]}
            aria-label={stepAtIndex.label}
            data-testid="step-atom"
          >
            {renderStepIcon(stepIndex)}
          </a>
        );
      }

      if (shouldRenderToLinkAtom && stepAtIndex?.to) {
        const linkTarget = stepAtIndex.to;

        return (
          <Link
            to={linkTarget}
            css={[stepStyle(stepIndex), linkStepStyles]}
            aria-label={stepAtIndex.label}
            data-testid="step-atom"
          >
            {renderStepIcon(stepIndex)}
          </Link>
        );
      }

      return (
        <div css={stepStyle(stepIndex)} data-testid="step-atom">
          {renderStepIcon(stepIndex)}
        </div>
      );
    };

    return renderTooltip(renderAtomType());
  };

  const renderEachLabel = (i: number) => {
    if (showStatusLabel || !stepsHaveLabels) return null;

    return (
      <Typography
        key={`step-label-${i}`}
        css={[styles.labels.all, isCurrentErrorStep(i + 1) && styles.labels.error]}
      >
        {renderErrorLabelIcon(i)}
        {stepLabels[i]}
      </Typography>
    );
  };

  const renderSteps = () => {
    return Array.from({ length: stepCount }).map((_, i) => (
      <div key={`step-${i}`} css={stepWrapperStyle(i + 1)}>
        {renderStep(i)}
        {renderEachLabel(i)}
      </div>
    ));
  };

  const renderCurrentStatusLabel = () => {
    if (!showStatusLabel) return null;

    return (
      <Typography
        css={statusLabelStyle(currentStep)}
        sizeOverride={size === "large" ? "h4" : undefined}
      >
        {renderErrorLabelIcon(currentStep - 1)}
        {stepLabels[currentStep - 1]}
      </Typography>
    );
  };

  return (
    <div {...rest}>
      <div css={styles.stepperWrapper}>{renderSteps()}</div>
      {renderCurrentStatusLabel()}
    </div>
  );
};

export { Stepper, type Props as StepperProps };
